import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppCheck } from '@angular/fire/app-check';
import {
  and,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  Query,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  getAI,
  getGenerativeModel,
  getImagenModel,
  VertexAIBackend,
} from 'firebase/ai';
import { getApp } from 'firebase/app';
import {
  fetchAndActivate,
  getRemoteConfig,
  getString,
} from 'firebase/remote-config';
import { intersects } from 'radash';
import { catchError, from, map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import {
  GroupNoticeSkillsConfig,
  GroupNoticeStyle,
} from '../models/group-notice-style.model';
import { SchoolMemberType } from '../models/member.school.model';
import { MemberType } from '../models/membership.status';
import { NoticeAiData, NoticeAiDataSchema } from '../models/notice-zod.model';
import { Notice, NoticeStatus } from '../models/notice.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class NoticeService {
  http = inject(HttpClient);
  afs = inject(Firestore);
  utilsService = inject(UtilsService);
  appCheck = inject(AppCheck, { optional: true });

  constructor() {}

  getNoticesByGroup({
    groupId,
    memberType,
    academicYear,
    startDate,
    limitRecords,
    gradeIds,
    lastRecord,
  }: {
    groupId: string;
    memberType: string[];
    academicYear?: number;
    startDate?: Timestamp;
    limitRecords?: number;
    gradeIds?: string[] | undefined;
    lastRecord?: Notice;
  }): Observable<Notice[]> {
    console.log(
      `NoticeService :: getNoticesByGroup: GroupId: ${groupId}, MemberType: ${memberType}, gradeIds: ${gradeIds}, Limit: ${limitRecords}`,
    );

    // Build the query using modular API
    const noticesRef = collection(this.afs, 'groups', groupId, 'notices');
    let q: Query<DocumentData> = query(noticesRef);

    // Role-based filtering
    const isAdmin = intersects(memberType, [
      SchoolMemberType.president,
      SchoolMemberType.principal,
      MemberType.admin,
    ]);

    if (!isAdmin) {
      q = query(q, where('noticeStatus', '==', NoticeStatus.active));

      const filterNotices: string[] = [];
      if (memberType.includes(SchoolMemberType.student))
        filterNotices.push(SchoolMemberType.student);
      if (memberType.includes(SchoolMemberType.parent))
        filterNotices.push(SchoolMemberType.parent);
      if (memberType.includes(SchoolMemberType.teacher))
        filterNotices.push(SchoolMemberType.teacher);
      if (memberType.includes(MemberType.member))
        filterNotices.push(MemberType.member);
      if (memberType.includes(MemberType.treasurer))
        filterNotices.push(MemberType.treasurer);

      if (filterNotices.length > 0) {
        q = query(q, where('memberType', 'array-contains-any', filterNotices));
      }
    }

    // Grade filtering
    if (!isAdmin && gradeIds) {
      q = query(
        q,
        or(
          where('isGradeNotice', '==', false),
          and(
            where('isGradeNotice', '==', true),
            where('gradeId', 'in', gradeIds),
          ),
        ),
      );
      // } else if (!isAdmin) {
      //   q = query(q, where('isGradeNotice', '==', false));
    }

    // Date filter
    const fromDate =
      startDate ?? Timestamp.fromDate(dayjs().subtract(6, 'month').toDate());
    q = query(q, where('publishOn', '>=', fromDate));

    // Academic year (optional)
    // if (academicYear) {
    //   q = query(q, where('academicYear', '==', academicYear));
    // }

    // Ordering & pagination
    q = query(q, orderBy('publishOn', 'desc'));
    if (limitRecords) q = query(q, limit(limitRecords));
    if (lastRecord?.publishOn) {
      q = query(q, startAfter(lastRecord.publishOn));
    }

    // Execute query (modular getDocs)
    console.log(
      'NoticeService :: Executing query on path:',
      `groups/${groupId}/notices`,
    );
    return from(getDocs(q)).pipe(
      map((querySnapshot) => {
        console.log(
          `NoticeService :: querySnapshot docs length: ${querySnapshot.docs.length}`,
        );
        return querySnapshot.docs.map((doc) => {
          const data = doc.data() as Notice;
          data.id = doc.id;
          return data;
        });
      }),
      catchError((error: any) => {
        console.error(`NoticeService :: getNoticesByGroup: Error:`, error);
        const appException: AppExceptionModel = {
          code: 'ERR_GET_NOTICE_BY_GROUP',
          message:
            'Unable to fetch Notices at this time. Please try again later',
          title: 'Error Fetching Notices',
        };
        throw appException;
      }),
    );
  }

  /**
   * Get a single notice by ID
   */
  getNoticeById(groupId: string, id: string): Observable<Notice> {
    const noticeRef = doc(this.afs, 'groups', groupId, 'notices', id);

    return from(getDoc(noticeRef)).pipe(
      map((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error(`Notice with id ${id} not found`);
        }
        const data = docSnap.data() as Notice;
        data.id = docSnap.id;
        return data;
      }),
    );
  }

  /**
   * Create a notice inside a group (sub-collection)
   */
  async create(id: string, notice: any): Promise<void> {
    const noticeRef = doc(this.afs, 'groups', notice.groupId, 'notices', id);
    return setDoc(noticeRef, notice);
  }

  /**
   * Update a notice inside a group
   */
  async update(
    groupId: string,
    id: string,
    notice: Partial<Notice>,
  ): Promise<void> {
    const noticeRef = doc(this.afs, 'groups', groupId, 'notices', id);
    return updateDoc(noticeRef, notice);
  }

  /**
   * Delete a notice inside a group
   */
  async delete(groupId: string, id: string): Promise<void> {
    const noticeRef = doc(this.afs, 'groups', groupId, 'notices', id);

    // Delete S3 notice folder and attachments first
    try {
      const headers = await this.utilsService.setAxiosHeaders(true);
      await axios.post(
        environment['AWS_SCHOOL_NOTICE_DELETE']!,
        {
          groupId: groupId,
          noticeId: id,
        },
        {
          headers: headers,
        },
      );
    } catch (err) {
      console.error('Failed to clean up S3 notice folder on delete:', err);
    }

    // Delete all related calendar events
    const calendarCollRef = collection(
      this.afs,
      'groups',
      groupId,
      'events_calendar',
    );
    const q = query(calendarCollRef, where('refId', '==', id));
    try {
      const snap = await getDocs(q);
      const deletePromises = snap.docs.map((docSnap) => deleteDoc(docSnap.ref));
      await Promise.all(deletePromises);
    } catch (err) {
      console.error('Failed to clean up calendar sub-events on delete:', err);
    }

    return deleteDoc(noticeRef);
  }

  async getNoticeStats(groupId: string): Promise<any> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'noticeCount', {
        limitedUseAppCheckTokens: true,
      });
      const result = await callable({
        groupId,
      });
      return result.data;
    } catch (error) {
      return Promise.reject('Unable to fetch Notice Statistics');
    }
  }

  async getAiNoticeTextPromptTemplate(): Promise<string> {
    try {
      const app = getApp();
      const remoteConfig = getRemoteConfig(app);
      await fetchAndActivate(remoteConfig);
      const prompt = getString(remoteConfig, 'notice_text_gen');
      if (prompt && prompt.trim().length > 0) {
        return prompt;
      }
    } catch (err) {
      console.warn(
        'NoticeService :: Could not fetch notice_text_gen template from Remote Config, using default prompt',
        err,
      );
    }
    return `You are an expert Notice Creation Assistant for community, church, and Sunday School groups. Your job is to generate structured notice details based on user input, conversation context, and group guidelines.

Strict Guidelines:
1. Event Date Rules:
   - If the user specifies dates for an event (e.g. event start date and end date), set publishOn to a date and time BEFORE the event starts so members receive advance notice.
   - Set removeAfter (publish till) to the exact date and time when the event ends.

2. Sunday School / School Audience & Role Restrictions:
   - If this is a Sunday School or School group (groupType is 'school') and the user is NOT an admin/principal/president but is ONLY a grade teacher:
     * They can ONLY send notices to their assigned grade.
     * You MUST set isGradeNotice to true, set gradeId to the user's assigned grade ID, set gradeName to the user's assigned grade name, and restrict memberType to their grade audience.

3. Image Requirement Evaluation (requiresImageGen):
   - Set requiresImageGen to true ONLY if:
     a) This is the first time a notice request is received (hasExistingImage is false) and the user did NOT ask "no image".
     b) The user explicitly asks to generate, update, redesign, modify, or change the notice image (e.g. 'change image', 'new poster', 'redesign banner', 'make background blue', 'use beach theme').
   - Set requiresImageGen to false if:
     a) An image already exists (hasExistingImage is true) and the user is making text/date/audience edits without asking for image changes.
     b) The user explicitly asks NOT to generate an image (e.g. 'no image', 'without image', 'text only').

4. Visual Theme & Instruction Extraction (imageThemeDescription):
   - If requiresImageGen is true:
     * Read the user chat input carefully. If the user provided any specific visual instructions, style choices, background theme, colors, subject matter, or mood (e.g., 'sunny park', 'blue ocean background', 'candlelight', 'nature scene'), extract and summarize them into imageThemeDescription.
     * If the user did NOT provide explicit visual instructions, generate a default, creative, highly relevant visual theme description for imageThemeDescription based on the notice title and description (e.g., for 'Sunday School Picnic', default theme: 'sunny outdoor park with green trees, picnic table, and bright sky').
     * By default, design imageThemeDescription for a detailed, warm storybook illustration style unless the user explicitly requested another style (such as realistic photography, flat vector, 3D art, etc.).
   - If requiresImageGen is false:
     * Leave imageThemeDescription empty or null.

5. Formatting Rules:
   - Extract notice title (3-100 characters), notice description (10-5000 characters), publish dates, target audience (memberType), notice status, and grade notice details.
   - Format publishOn and removeAfter as datetime strings formatted like YYYY-MM-DDTHH:mm.
   - Return a valid JSON adhering strictly to the JSON schema.`;
  }

  async getAiNoticeImagePromptTemplate(): Promise<string> {
    try {
      const app = getApp();
      const remoteConfig = getRemoteConfig(app);
      await fetchAndActivate(remoteConfig);
      const prompt = getString(remoteConfig, 'notice_image_gen');
      if (prompt && prompt.trim().length > 0) {
        return prompt;
      }
    } catch (err) {
      console.warn(
        'NoticeService :: Could not fetch notice_image_gen template from Remote Config, using default prompt',
        err,
      );
    }
    return `You are an expert AI Visual Art Director specializing in notice posters and event banners for community, church, and Sunday School groups.
Your job is to compose a detailed, high-quality image generation prompt for Imagen / Nano Banana models.

Prompt Construction Rules:
1. STORYBOOK ILLUSTRATION DEFAULT:
   - By default, generate a detailed, warm storybook illustration, detailed vector art, or watercolor sketch style with charming characters and beautiful watercolor textures.
   - Do NOT use realistic photography or photorealistic styles UNLESS the user explicitly requested a photographic style (e.g. "photograph", "real photo", "photorealistic", "camera shot") in User Style/Instruction, Visual Theme, or Visual Style Guidelines.

2. DYNAMIC STYLE OVERRIDE:
   - Carefully read the User Style/Instruction, Visual Theme, and Visual Style Guidelines. If the user mentions any specific artistic style (e.g. "3D render", "oil painting", "flat vector design", "neon cyberpunk", "crayon drawing", or "real-world photo"), you MUST override the default storybook illustration style and generate the prompt using the requested style.

3. USER INSTRUCTION INTEGRATION:
   - Carefully read the user chat input (User Style/Instruction) and Visual Theme. Incorporate all specific subject elements, mood, background setting, colors, and visual instructions provided by the user.
   - If the user has NOT provided specific visual instructions, compose a default storybook illustration scene prompt tailored to the notice title, notice body description, and group context.

4. POSTER BACKGROUND COMPOSITION:
   - Create a detailed, vibrant image prompt optimized for a 3:4 aspect ratio mobile portrait notice card background.
   - Focus on clean visual composition, well-balanced natural lighting, harmonious color palette, and a clear focal subject.

5. TEXT OVERLAY RULES:
   - If the user explicitly requests text written on the image (e.g. "write the title on the image", "add text overlay", "put the date on the image"), you MUST include that text as a bold, legible typographic overlay in the prompt. Describe the exact text content, font style (e.g. bold sans-serif), placement (e.g. bottom-center, top-left), and contrast colour (white text on dark area, or dark text on light area).
   - If the user has NOT requested text on the image, DO NOT include rendered text, logos, watermarks, or graphic design badges — keep it a clean visual background for software-rendered text overlays.`;
  }

  async generateNoticeWithAi({
    prompt,
    groupId,
    groupName,
    selectedStyleId,
    shouldGenerateImage = true,
    chatHistory = [],
    userInfo,
    hasExistingImage = false,
  }: {
    prompt: string;
    groupId: string;
    groupName?: string;
    selectedStyleId?: string;
    shouldGenerateImage?: boolean;
    chatHistory?: { role: 'user' | 'model'; parts: { text: string }[] }[];
    userInfo?: {
      isAdmin: boolean;
      isTeacherOnly: boolean;
      gradeId?: string;
      gradeName?: string;
      groupType?: string;
    };
    hasExistingImage?: boolean;
  }): Promise<{
    noticeData: NoticeAiData;
    rawReply: string;
    exactCredits?: number;
    tokens?: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    };
  }> {
    const app = getApp();
    const backend = new VertexAIBackend();
    const ai = getAI(app, { backend });

    // Fetch dynamic base system prompt from Remote Config template notice_text_gen
    const baseSystemPrompt = await this.getAiNoticeTextPromptTemplate();

    // Fetch group notice skills guidelines and active visual style from Firestore groups/{groupId}/notice_skills
    let groupSkillsPrompt = '';
    let stylePrompt = '';

    try {
      const skillsConfig = await this.getGroupNoticeSkillsConfig(groupId);
      if (skillsConfig?.skillsPrompt) {
        groupSkillsPrompt = `\n\nGroup Notice Image Guidelines (skills.md):\n${skillsConfig.skillsPrompt}`;
      }

      if (selectedStyleId) {
        const styleDoc = await getDoc(
          doc(this.afs, 'groups', groupId, 'notice_skills', selectedStyleId),
        );
        if (styleDoc.exists()) {
          const style = styleDoc.data() as GroupNoticeStyle;
          stylePrompt = `\n\nSelected Notice Image Visual Style (${style.name}):\n${style.promptTemplate}`;
        }
      }
    } catch (err) {
      console.warn(
        'NoticeService :: Error fetching group notice skills from Firestore:',
        err,
      );
    }

    const userRoleContext = userInfo
      ? `\n\nUser Context:\n- Group Type: ${userInfo.groupType || 'school'}\n- Is Admin: ${userInfo.isAdmin}\n- Is Grade Teacher Only: ${userInfo.isTeacherOnly}\n- Assigned Grade ID: ${userInfo.gradeId || 'None'}\n- Assigned Grade Name: ${userInfo.gradeName || 'None'}`
      : '';

    const imageStateContext = `\n- Has Existing Notice Image: ${hasExistingImage}`;

    const fullSystemInstruction = `${baseSystemPrompt}${userRoleContext}${imageStateContext}${groupSkillsPrompt}${stylePrompt}\nToday's date and time: ${dayjs().format('YYYY-MM-DDTHH:mm')} (${dayjs().format('dddd, MMMM D, YYYY')})\nGroup Name: ${groupName || 'Group'}\nUser Image Preference: ${shouldGenerateImage ? 'Generate notice banner image including imageThemeDescription' : 'Do not generate image, but still fill imageThemeDescription with a relevant visual theme for this notice'}`;

    const responseSchema = {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Notice title (3-100 chars)' },
        description: {
          type: 'string',
          description: 'Notice body text (10-5000 chars)',
        },
        publishOn: {
          type: 'string',
          description: 'Publish datetime ISO string (YYYY-MM-DDTHH:mm)',
        },
        removeAfter: {
          type: 'string',
          description: 'Publish till datetime ISO string (YYYY-MM-DDTHH:mm)',
        },
        noticeStatus: {
          type: 'string',
          enum: ['active', 'draft', 'completed'],
        },
        isGradeNotice: { type: 'boolean' },
        gradeId: { type: 'string' },
        gradeName: { type: 'string' },
        memberType: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Audience roles array, e.g. ["admin", "member", "treasurer"] or ["parent", "student", "teacher"]',
        },
        requiresImageGen: {
          type: 'boolean',
          description:
            'True ONLY if initial creation or user explicitly asked for image changes',
        },
        imageThemeDescription: {
          type: 'string',
          description: 'Visual theme subject if image generation is required',
        },
        imagePrompt: {
          type: 'string',
          description: 'Prompt describing visual banner image',
        },
        shouldGenerateImage: {
          type: 'boolean',
          description:
            'True ONLY if initial creation or user explicitly asked for image changes',
        },
      },
      required: [
        'title',
        'description',
        'noticeStatus',
        'memberType',
        'requiresImageGen',
      ],
    };

    const model = getGenerativeModel(ai, {
      model: 'gemini-2.5-flash',
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: responseSchema as any,
      },
      systemInstruction: fullSystemInstruction,
    });

    const chatSession = model.startChat({ history: chatHistory });
    const response = await chatSession.sendMessage(prompt);
    const textResponse = response.response.text();

    const parsedJson = JSON.parse(textResponse);
    const validatedData = NoticeAiDataSchema.parse(parsedJson);

    const usage = response.response.usageMetadata;
    const promptTokens =
      usage?.promptTokenCount || Math.ceil(prompt.length / 4);
    const completionTokens =
      usage?.candidatesTokenCount || Math.ceil(textResponse.length / 4);
    const exactCredits = Number(
      ((promptTokens / 1000) * 1 + (completionTokens / 1000) * 2).toFixed(4),
    );

    return {
      noticeData: validatedData,
      rawReply: textResponse,
      exactCredits,
      tokens: {
        promptTokens,
        completionTokens,
        totalTokens: promptTokens + completionTokens,
      },
    };
  }

  /**
   * Classifies a user's chat message and routes it to the correct action using Gemini function calling.
   * The model is forced to pick one of the declared tools (mode: ANY), so this always returns a toolName.
   */
  async classifyNoticeIntent({
    prompt,
    chatHistory = [],
    currentFormState = '',
    groupName,
    hasExistingImage = false,
    hasDraft = false,
    userInfo,
    isImageEditorOpen = false,
    imageLayers = [],
  }: {
    prompt: string;
    chatHistory?: { role: 'user' | 'model'; parts: { text: string }[] }[];
    currentFormState?: string;
    groupName?: string;
    hasExistingImage?: boolean;
    hasDraft?: boolean;
    userInfo?: {
      isAdmin: boolean;
      isTeacherOnly: boolean;
      gradeId?: string;
      gradeName?: string;
      groupType?: string;
    };
    /** Whether the image editor overlay is currently open on screen. */
    isImageEditorOpen?: boolean;
    /** Short summaries of the editor's current layers, so layer-targeted edits can be grounded in a real layerId. */
    imageLayers?: { id: string; type: string; label: string }[];
  }): Promise<{ toolName: string; toolArgs: any }> {
    const app = getApp();
    const backend = new VertexAIBackend();
    const ai = getAI(app, { backend });

    const systemInstruction = `You are an AI assistant for notice creation and management in a church/school group app.
Based on the user's message, call the most appropriate function. Always call exactly one function.

Current notice state:
${currentFormState || 'No notice drafted yet.'}

Group: ${groupName || 'Group'}
Has existing image: ${hasExistingImage}
Has existing draft: ${hasDraft}
${userInfo ? `User role — Admin: ${userInfo.isAdmin}, Teacher only: ${userInfo.isTeacherOnly}, Grade: ${userInfo.gradeName || 'N/A'}` : ''}

Image editor open: ${isImageEditorOpen}
Image editor layers: ${imageLayers.length > 0 ? imageLayers.map((l) => `${l.id} (${l.type}: "${l.label}")`).join(', ') : 'none yet'}

Routing rules:
- generate_notice: User wants to create, draft, or completely rewrite a notice. Use this for new notices or full rewrites.
- patch_notice_fields: User wants to change one or more specific form fields (e.g. "change the date", "update the title", "add parking info to the description", "set status to draft"). Return the COMPLETE updated value for each patched field — for description, return the full text including any additions merged with the existing content. Do NOT use this for requests about the image itself — those always go through the image tools below.
- generate_image: User wants a new or completely different background image/poster (e.g. "generate an image", "change the background", "use a beach theme", "make it more colourful"). This opens the image editor automatically if it isn't already open.
- open_image_editor: User wants to look at or start working on the image without necessarily generating anything yet (e.g. "let me see the image", "open the editor").
- close_image_editor: User wants to stop editing the image and get back to the form (e.g. "close the editor", "I'm done with the image").
- edit_image_layer: User wants to change something about an EXISTING layer already listed under "Image editor layers" above — resize, recolor, reposition, or reword it (e.g. "make the title bigger", "move the date to the bottom", "change the title text to ..."). Set layerId to the matching id from the layers list. This also opens the editor if it isn't already open.
- add_image_layer: User wants to add something new to the image that isn't a full background regeneration — a text block, a QR code (use their meeting/link URL as targetUrl), a logo, or a decorative element (e.g. "add a QR code to the Zoom link", "put our logo in the corner", "add a line with the venue"). This also opens the editor if it isn't already open.
- remove_image_layer: User wants to delete an existing layer listed under "Image editor layers" (e.g. "remove the QR code", "delete that text box").
- save_notice: User wants to save, publish, or submit. Use for: "save it", "publish", "submit", "looks good", "go ahead", "done", "that's fine".
- answer_question: User is asking a question about the current notice or wants information without making changes.`;

    const functionDeclarations: any[] = [
      {
        name: 'generate_notice',
        description: 'Generate a full notice draft when the user wants to create or completely rewrite a notice.',
        parameters: { type: 'object', properties: {}, required: [] },
      },
      {
        name: 'patch_notice_fields',
        description: 'Update one or more specific notice fields without regenerating everything. Use for targeted edits.',
        parameters: {
          type: 'object',
          properties: {
            title: { type: 'string', description: 'Complete updated notice title' },
            description: { type: 'string', description: 'Complete updated notice description (full text, not just the addition)' },
            publishOn: { type: 'string', description: 'Updated publish datetime in YYYY-MM-DDTHH:mm format' },
            removeAfter: { type: 'string', description: 'Updated remove-after datetime in YYYY-MM-DDTHH:mm format' },
            noticeStatus: { type: 'string', enum: ['active', 'draft', 'completed'], description: 'Updated notice status' },
            memberType: { type: 'array', items: { type: 'string' }, description: 'Updated audience roles array' },
            isGradeNotice: { type: 'boolean', description: 'Whether this is a grade-specific notice' },
            gradeId: { type: 'string', description: 'Grade ID if isGradeNotice is true' },
            gradeName: { type: 'string', description: 'Grade name if isGradeNotice is true' },
            assistantMessage: { type: 'string', description: 'Friendly confirmation message describing what was updated' },
          },
          required: ['assistantMessage'],
        },
      },
      {
        name: 'generate_image',
        description: 'Generate a brand new background image, replacing the current one entirely. Opens the image editor if not already open. Use when the user wants a different/new background, not a small tweak to the existing one.',
        parameters: {
          type: 'object',
          properties: {
            imageTheme: { type: 'string', description: 'Visual theme, style, or mood instructions extracted from the user message' },
            assistantMessage: { type: 'string', description: 'Confirmation message shown in chat' },
          },
          required: ['assistantMessage'],
        },
      },
      {
        name: 'open_image_editor',
        description: 'Open the image editor without generating or changing anything, so the user can look at or manually work on the image.',
        parameters: {
          type: 'object',
          properties: {
            assistantMessage: { type: 'string', description: 'Message shown in chat' },
          },
          required: ['assistantMessage'],
        },
      },
      {
        name: 'close_image_editor',
        description: 'Close the image editor and return to the notice form.',
        parameters: {
          type: 'object',
          properties: {
            assistantMessage: { type: 'string', description: 'Message shown in chat' },
          },
          required: ['assistantMessage'],
        },
      },
      {
        name: 'edit_image_layer',
        description: 'Modify an existing layer on the image (resize, recolor, reposition, or reword). layerId MUST match one of the ids listed under "Image editor layers" in the system context. Opens the editor if not already open.',
        parameters: {
          type: 'object',
          properties: {
            layerId: { type: 'string', description: 'The id of the layer to modify, taken from the current layers list' },
            updates: {
              type: 'object',
              description: 'Only the properties that should change, e.g. { "text": "New title", "fontSize": 32, "color": "#ffffff" }',
              properties: {
                text: { type: 'string', description: 'New text content, if this is a text layer' },
                fontSize: { type: 'number', description: 'New font size in px, if this is a text layer' },
                fontFamily: { type: 'string', description: 'New font family, if this is a text layer' },
                color: { type: 'string', description: 'New text or fill color as a hex code' },
                x: { type: 'number', description: 'New horizontal position, 0-1 fraction of canvas width' },
                y: { type: 'number', description: 'New vertical position, 0-1 fraction of canvas height' },
                width: { type: 'number', description: 'New width, 0-1 fraction of canvas width' },
                height: { type: 'number', description: 'New height, 0-1 fraction of canvas height' },
                rotation: { type: 'number', description: 'New rotation in degrees' },
              },
            },
            assistantMessage: { type: 'string', description: 'Confirmation message shown in chat' },
          },
          required: ['layerId', 'updates', 'assistantMessage'],
        },
      },
      {
        name: 'add_image_layer',
        description: 'Add a new layer to the image: a text block, a QR code, a logo, or a decorative element. Opens the editor if not already open.',
        parameters: {
          type: 'object',
          properties: {
            layerType: { type: 'string', enum: ['text', 'photo', 'logo', 'qr', 'decoration'], description: 'Kind of layer to add' },
            props: {
              type: 'object',
              description: 'Layer-specific content, e.g. { "text": "Park Pavilion" } for text, or { "targetUrl": "https://..." } for a QR code',
              properties: {
                text: { type: 'string', description: 'Text content, for a text layer' },
                targetUrl: { type: 'string', description: 'The URL the QR code should link to, for a qr layer' },
              },
            },
            assistantMessage: { type: 'string', description: 'Confirmation message shown in chat' },
          },
          required: ['layerType', 'assistantMessage'],
        },
      },
      {
        name: 'remove_image_layer',
        description: 'Remove an existing layer from the image. layerId MUST match one of the ids listed under "Image editor layers" in the system context.',
        parameters: {
          type: 'object',
          properties: {
            layerId: { type: 'string', description: 'The id of the layer to remove, taken from the current layers list' },
            assistantMessage: { type: 'string', description: 'Confirmation message shown in chat' },
          },
          required: ['layerId', 'assistantMessage'],
        },
      },
      {
        name: 'save_notice',
        description: 'Save or publish the notice to the database. Use when the user confirms they are done.',
        parameters: {
          type: 'object',
          properties: {
            assistantMessage: { type: 'string', description: 'Confirmation message shown in chat' },
          },
          required: ['assistantMessage'],
        },
      },
      {
        name: 'answer_question',
        description: 'Answer a question or provide information about the current notice without making any changes.',
        parameters: {
          type: 'object',
          properties: {
            assistantMessage: { type: 'string', description: 'The answer or information to display in the chat' },
          },
          required: ['assistantMessage'],
        },
      },
    ];

    const model = getGenerativeModel(ai, {
      model: 'gemini-2.5-flash',
      tools: [{ functionDeclarations }] as any,
      toolConfig: {
        functionCallingConfig: {
          mode: 'ANY' as any,
        },
      } as any,
      systemInstruction,
    });

    const chatSession = model.startChat({ history: chatHistory });
    const response = await chatSession.sendMessage(prompt);

    const functionCalls = (response.response as any).functionCalls?.();
    if (functionCalls && functionCalls.length > 0) {
      const fc = functionCalls[0];
      return { toolName: fc.name, toolArgs: fc.args || {} };
    }

    // Fallback: should not happen with mode ANY, but default to full generation
    return { toolName: 'generate_notice', toolArgs: {} };
  }

  /**
   * Prompt 2: Separate prompt call using notice_image_gen Remote Config template
   * Executed ONLY when notice text generation evaluates `requiresImageGen === true`
   */
  async generateNoticePosterPrompt({
    noticeTitle,
    noticeDescription,
    imageThemeDescription,
    userPrompt,
    groupName,
    styleTemplate,
  }: {
    noticeTitle: string;
    noticeDescription?: string;
    imageThemeDescription?: string;
    userPrompt?: string;
    groupName?: string;
    styleTemplate?: string;
  }): Promise<string> {
    const app = getApp();
    const backend = new VertexAIBackend();
    const ai = getAI(app, { backend });

    const baseSystemPrompt = await this.getAiNoticeImagePromptTemplate();
    const systemInstruction = `${baseSystemPrompt}

Group Name: ${groupName || 'Group'}
Notice Title: ${noticeTitle}
Notice Description: ${noticeDescription || ''}
Visual Theme: ${imageThemeDescription || ''}
User Instruction: ${userPrompt || ''}
Visual Style Guidelines: ${styleTemplate || ''}`;

    const responseSchema = {
      type: 'object',
      properties: {
        imagePrompt: {
          type: 'string',
          description: 'Detailed visual image prompt',
        },
      },
      required: ['imagePrompt'],
    };

    const model = getGenerativeModel(ai, {
      model: 'gemini-2.5-flash',
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: responseSchema as any,
      },
      systemInstruction,
    });

    const res = await model.generateContent(
      `Create image generation prompt for notice "${noticeTitle}". Visual Theme/Instruction: "${imageThemeDescription || ''}". User instruction: "${userPrompt || ''}"`,
    );
    const parsed = JSON.parse(res.response.text());
    return (
      parsed.imagePrompt || `${noticeTitle} ${imageThemeDescription || ''}`
    );
  }

  async generateNoticeImage(
    prompt: string,
    stylePrompt?: string,
    groupId?: string,
    noticeTitle?: string,
    noticeId?: string,
  ): Promise<string> {
    // If the user requested text on the image, don't dilute the intent with generic quality modifiers
    const lowerPrompt = prompt.toLowerCase();
    const wantsTextOnImage =
      lowerPrompt.includes('text') ||
      lowerPrompt.includes('write') ||
      lowerPrompt.includes('overlay') ||
      lowerPrompt.includes('title on') ||
      lowerPrompt.includes('words on') ||
      lowerPrompt.includes('label');

    const qualityModifiers = '3:4 aspect ratio, mobile portrait notice background, high resolution, studio quality';
    const combinedPrompt = wantsTextOnImage
      ? `${prompt} ${stylePrompt || ''}`.trim()
      : `${prompt} ${stylePrompt || ''} ${qualityModifiers}`.trim();

    let generatedImageUrl: string | undefined;
    let generatedViaCloudFunction = false;


    // 1. Primary: Call secure notice_generateImageNanoBanana Cloud Function (has full GCP Service Account credentials)
    try {
      const functions = getFunctions();
      const callable = httpsCallable<
        {
          prompt: string;
          stylePrompt?: string;
          groupId?: string;
          noticeTitle?: string;
          noticeId?: string;
        },
        { imageUrl: string; success: boolean }
      >(functions, 'notice_generateImageNanoBanana', {
        limitedUseAppCheckTokens: true,
      });

      // Pass the notice's own title/id through so the server-side credit ledger entry can reference
      // them instead of the underlying AI vendor/model name or a raw groupId.
      const result = await callable({ prompt, stylePrompt, groupId, noticeTitle, noticeId });
      if (result.data?.imageUrl) {
        generatedImageUrl = result.data.imageUrl;
        generatedViaCloudFunction = true;
        console.log(
          'NoticeService :: Nano Banana image successfully generated via Cloud Function.',
        );
      }
    } catch (fnErr: any) {
      console.warn(
        'NoticeService :: Cloud Function call unavailable or skipped:',
        fnErr?.message || fnErr,
      );
    }

    // 2. Secondary: Client-side Firebase AI Imagen SDK (handles App Check & Auth natively)
    if (!generatedImageUrl) {
      try {
        const app = getApp();
        const backend = new VertexAIBackend();
        const ai = getAI(app, { backend });
        const modelsToTry = [
          'imagen-3.0-generate-002',
          'imagen-3.0-generate-001',
          'imagen-3.0-fast-generate-001',
          'imagegeneration@006',
          'imagegeneration@005',
        ];

        for (const modelName of modelsToTry) {
          try {
            const imagenModel = getImagenModel(ai, {
              model: modelName,
            });
            const res = await imagenModel.generateImages(combinedPrompt);
            if (
              res.images &&
              res.images.length > 0 &&
              res.images[0].bytesBase64Encoded
            ) {
              generatedImageUrl = `data:image/jpeg;base64,${res.images[0].bytesBase64Encoded}`;
              console.log(
                `NoticeService :: Generated image client-side via Firebase AI Imagen SDK with model ${modelName}`,
              );
              break;
            }
          } catch (modelErr: any) {
            console.warn(
              `NoticeService :: Client-side Firebase AI Imagen model ${modelName} attempt:`,
              modelErr?.message || modelErr,
            );
          }
        }
      } catch (err: any) {
        console.warn(
          'NoticeService :: Client-side Firebase AI Imagen generation encountered error:',
          err?.message || err,
        );
      }
    }

    // 3. Fallback demo URL if both fail
    if (!generatedImageUrl) {
      const encodedPrompt = encodeURIComponent(combinedPrompt);
      const randomSeed = Math.floor(Math.random() * 1000000);
      generatedImageUrl = `https://image.pollinations.ai/prompt/${encodedPrompt}?width=810&height=1080&model=flux&nologo=true&seed=${randomSeed}`;
    }

    // 4. Log AI credit usage ONLY if generated client-side (since Cloud Function already handles credit logging when it succeeds)
    // The transaction description intentionally avoids naming the underlying AI model/vendor and avoids
    // raw internal identifiers (like a bare groupId) — instead it references the notice's own title/id so a
    // member can recognize the charge and raise a dispute if needed.
    if (groupId && !generatedViaCloudFunction) {
      const businessReference = noticeTitle
        ? `Notice: "${noticeTitle}"${noticeId ? ` (ID: ${noticeId})` : ''}`
        : undefined;
      this.recordAiCreditUsage(
        groupId,
        'notice_image_generation',
        2.5,
        businessReference,
      ).catch((err) => {
        console.warn('NoticeService :: Credit tracking log failed:', err);
      });
    }

    return generatedImageUrl;
  }

  /**
   * Log AI credit usage and ledger entry in Firestore via deductGroupAiCredits Cloud Function
   */
  async recordAiCreditUsage(
    groupId: string,
    feature: string,
    creditsDeducted: number = 1,
    promptSummary?: string,
  ): Promise<void> {
    if (!groupId) return;

    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'deductGroupAiCredits', {
        limitedUseAppCheckTokens: true,
      });
      await callable({
        groupId,
        feature,
        creditsDeducted: Number(creditsDeducted),
        promptSummary: (promptSummary || '').slice(0, 100),
      });
      console.log(
        `NoticeService :: Deducted ${creditsDeducted} AI credits via deductGroupAiCredits for group ${groupId}`,
      );
    } catch (logErr: any) {
      console.log(
        `NoticeService :: Client credit usage log skipped: ${logErr?.message || logErr}`,
      );
    }
  }

  async getGroupNoticeSkills(groupId: string): Promise<GroupNoticeStyle[]> {
    const collRef = collection(this.afs, 'groups', groupId, 'notice_skills');
    const snap = await getDocs(collRef);
    return snap.docs
      .filter((d) => d.id !== 'config')
      .map((d) => ({ id: d.id, ...d.data() }) as GroupNoticeStyle);
  }

  async saveGroupNoticeSkill(
    groupId: string,
    style: Partial<GroupNoticeStyle>,
    isDefault: boolean = false,
  ): Promise<string> {
    const collRef = collection(this.afs, 'groups', groupId, 'notice_skills');
    const docId = style.id || doc(collRef).id;
    const styleRef = doc(this.afs, 'groups', groupId, 'notice_skills', docId);
    await setDoc(
      styleRef,
      {
        ...style,
        id: docId,
        isDefault: isDefault || style.isDefault || false,
        updatedAt: Timestamp.now(),
      },
      { merge: true },
    );

    if (isDefault) {
      const configRef = doc(
        this.afs,
        'groups',
        groupId,
        'notice_skills',
        'config',
      );
      await setDoc(
        configRef,
        {
          defaultStyleId: docId,
          isConfig: true,
          updatedAt: Timestamp.now(),
        },
        { merge: true },
      );
    }
    return docId;
  }

  async getGroupNoticeSkillsConfig(
    groupId: string,
  ): Promise<GroupNoticeSkillsConfig | null> {
    const configRef = doc(
      this.afs,
      'groups',
      groupId,
      'notice_skills',
      'config',
    );
    const snap = await getDoc(configRef);
    if (snap.exists()) {
      return snap.data() as GroupNoticeSkillsConfig;
    }
    return null;
  }

  async updateGroupNoticeSkillsConfig(
    groupId: string,
    skillsPrompt: string,
  ): Promise<void> {
    const configRef = doc(
      this.afs,
      'groups',
      groupId,
      'notice_skills',
      'config',
    );
    await setDoc(
      configRef,
      {
        skillsPrompt,
        isConfig: true,
        updatedAt: Timestamp.now(),
      },
      { merge: true },
    );
  }
}
